import {
  RunEvent,
  RunResponse,
  SelectedAgent,
} from '@/features/ai-agents/shared/models';

/**
 * Interface for processing real-time chat events from AI agents
 * The processor can filter and handle specific events and generate appropriate "reasoning" chat messages
 * That will increase engagement & transparency and help users understand the agent's thought process.
 */
export interface RealtimeChatEventProcessor {
  /**
   * List of events this processor can handle
   * Set to empty array to disable processing
   * Set to undefined to process all events
   */
  eventFilter: RunEvent[] | undefined;

  /**
   * An optional event that, when received, will reset the internal state of the processor
   * This can be useful for events like RunEvent.AgentResponseContent to clear previous context
   * undefined means no reset event
   */
  resetEvent: RunEvent[] | undefined;

  /**
   * Process an incoming event and return the message to be displayed in the chat
   * @param agent the selected agent
   * @param event the incoming event data
   * @return the message to be displayed in the chat, or undefined if no message should be displayed
   */
  processEvent: (
    agent: SelectedAgent,
    event: RunResponse,
  ) => string | undefined;
}

export function shouldProcess(
  eventProcessor: RealtimeChatEventProcessor,
  event: RunResponse,
) {
  if (!eventProcessor) return false;
  if (!eventProcessor.eventFilter) return true;
  return eventProcessor.eventFilter.includes(event.event);
}

export function shouldReset(
  eventProcessor: RealtimeChatEventProcessor,
  event: RunResponse,
) {
  if (!eventProcessor) return false;
  if (!eventProcessor.resetEvent) return true;
  return eventProcessor.resetEvent.includes(event.event);
}

export function newEventProcessor(
  processEvent: (
    agent: SelectedAgent,
    event: RunResponse,
  ) => string | undefined,
  eventFilter: RunEvent[] | undefined,
  resetEvent: RunEvent[] | undefined = undefined,
): RealtimeChatEventProcessor {
  return {
    eventFilter,
    processEvent,
    resetEvent,
  };
}
